import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpBorService from "@plannel/services/fp/fp-bor-service";
import fpResourceService from "@plannel/services/fp/fp-resource-service";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

import ToolSupplyTabChart from "./ToolSupplyTabChart";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 1개(supplyTs) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "supplyTs", label: "FP_SUPPLY_TS" }
];

// old toolSupplyGridItems의 audit 4개(modifyBy/modifyDttm/createBy/createDttm)는 전부
// visible: false — 다른 그리드(BorTab 등)와 달리 대표 컬럼(수정일시)도 노출하지 않고 그룹
// 자체를 항상 숨긴다. 그룹+접기 대신 hide:true 플랫 컬럼으로 대응(접어도 펼쳐도 안 보임).
const auditHiddenFields = [
  { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", hide: true, type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime },
  { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", hide: true, type: ["nonEditableColumn"] },
  { headerName: "createdTs", field: "createDttm", colId: "createDttm", hide: true, type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime },
  { headerName: "createdBy", field: "createUser", colId: "createUser", hide: true, type: ["nonEditableColumn"] }
];

/**
 * 툴 공급 계획 탭 — old ToolSupplyTab.jsx 대응. 좌측 그리드(toolSupplyResourceGrid, 읽기전용)에서
 * 툴 자원 행을 클릭하면 그 자원의 입/출고 이력(우측 toolSupplyGrid)을 조회하고, 하단에
 * 누적 가용 수량 차트(ToolSupplyTabChart)를 그린다. 저장 시 old validationCheck와 동일하게
 * supplyTs 오름차순으로 정렬 후 IN/OUT 누적(totalCnt)을 재계산하고, 음수가 되면 저장을 막는다
 * (old도 매 저장마다 grid 전체를 다시 계산해 전송 — DataState 변경분만이 아니라 old와 동일하게
 * 매번 전체 행을 재전송한다).
 *
 * props: t, viewName, planScope, searchFields
 */
const ToolSupplyTab = ({ t, viewName, planScope, searchFields }) => {
  const resourceGridRef = useRef();
  const supplyGridRef = useRef();

  const [resources, setResources] = useState([]);
  const [toolSupply, setToolSupply] = useState([]);
  const [currentResource, setCurrentResource] = useState(null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const latestReqRef = useRef(0);

  const resourceGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_RESOURCE"), viewName: `${viewName}_RES` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);
  const supplyGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_TOOL_SUPPLY"), viewName: `${viewName}_SUPPLY` }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  const resourceColumnDefs = [
    // old headerText: "FP_TOOL_RESOURCE_CD"/"FP_TOOL_RESOURCE_NM" — 일반 자원이 아니라 "툴" 자원
    // 목록이라 FP_RESOURCE_CD/NM(범용 자원 라벨)이 아닌 전용 라벨을 쓴다.
    { headerName: "FP_TOOL_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", type: ["nonEditableColumn"], width: 150 },
    { headerName: "FP_TOOL_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 200 },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", type: ["nonEditableColumn"], width: 130 },
    { headerName: "FP_TOOL_CNT", field: "toolCnt", colId: "toolCnt", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 100 }
  ];

  const supplyColumnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_TOOL_RESOURCE_CD", field: "toolResourceCd", colId: "toolResourceCd", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_TOOL_CNT", field: "toolCnt", colId: "toolCnt", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_SUPPLY_CNT", field: "supplyCnt", colId: "supplyCnt", hide: true, type: ["nonEditableColumn"] },
    { headerName: "FP_SUPPLY_TS", field: "supplyTs", colId: "supplyTs", width: 170, type: ["dateTimeColumn", "centerAligned"] },
    { headerName: "FP_IN_OUT_TP", field: "inoutType", colId: "inoutType", width: 100, type: ["centerAligned"],
      cellEditor: "agSelectCellEditor", cellEditorParams: { values: ["IN", "OUT"] } },
    { headerName: "FP_CHANGE_QTY", field: "changeCnt", colId: "changeCnt", width: 100, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_TOTAL_CNT", field: "totalCnt", colId: "totalCnt", width: 100, type: ["nonEditableColumn", "numberColumn", "rightAligned"] },
    ...auditHiddenFields
  ];

  const retrieveResources = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    resourceGridRef.current?.api?.setGridOption("loading", true);

    fpResourceService.getAll({ planScope, resourceCdNm: searchFields.resourceCdNm, isTool: true })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = isEmpty(response.data) ? [] : response.data;
        setResources(results);
        if (results.length > 0) {
          setCurrentResource(results[0]);
          retrieveToolSupply(results[0]);
        } else {
          setCurrentResource(null);
          setToolSupply([]);
        }
      })
      .catch(e => debug(e))
      .finally(() => {
        if (reqId === latestReqRef.current) {
          resourceGridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const retrieveToolSupply = (resource) => {
    if (!planScope || !resource) return;
    supplyGridRef.current?.api?.setGridOption("loading", true);

    fpBorService.getToolSupplies({ planScope, resourceCd: resource.resourceCd })
      .then(response => {
        setToolSupply(isEmpty(response.data) ? [] : response.data);
        DataState.initialize(supplyGridRef.current?.api);
      })
      .catch(e => debug(e))
      .finally(() => {
        supplyGridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onResourceRowClicked = (e) => {
    if (!e.data) return;
    setCurrentResource(e.data);
    retrieveToolSupply(e.data);
  };

  // GridUtils.setColumnDefs를 거쳐야 헤더가 i18n 번역된다 — columnDefs를 그리드에 prop으로 직접
  // 넘기면 raw 키 문자열이 그대로 노출된다.
  const onResourceGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: resourceColumnDefs, viewName: `${viewName}_RES`, initState: true });
    retrieveResources();
  };

  const onSupplyGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs: supplyColumnDefs, viewName: `${viewName}_SUPPLY`, initState: true });
    DataState.initialize(params.api);
  };

  const insertRow = () => {
    if (!currentResource) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_TOOL_RESOURCE_ROW_SELECT") });
      return;
    }

    const newRow = {
      id: null, planScope, toolResourceCd: currentResource.resourceCd, toolCnt: currentResource.toolCnt,
      supplyCnt: 0, supplyTs: "", inoutType: "IN", changeCnt: 1, totalCnt: 0
    };
    DataState.setDataState(GridUtils.addRow(supplyGridRef.current.api, newRow));
  };

  // old validationCheck 대응: supplyTs 오름차순 정렬 후 자원의 toolCnt를 기준으로 IN/OUT 누적
  // totalCnt를 다시 계산. 하나라도 음수가 되면 저장을 막는다(null 반환). 계산된 값은 각 행에
  // 즉시 반영(node.setData)하고, 그 결과를 그대로 저장 payload로 사용한다(old도 매 저장마다
  // 그리드 전체를 다시 계산해 통째로 전송).
  const computeAndApplyTotals = () => {
    const nodes = [];
    supplyGridRef.current.api.forEachNode(node => nodes.push(node));
    const sorted = [...nodes].sort((a, b) => (a.data.supplyTs > b.data.supplyTs ? 1 : a.data.supplyTs < b.data.supplyTs ? -1 : 0));

    let cumulative = currentResource.toolCnt;
    for (const node of sorted) {
      const delta = node.data.inoutType === "IN" ? node.data.changeCnt : -node.data.changeCnt;
      cumulative += delta;
      if (cumulative < 0) return null;
    }

    cumulative = currentResource.toolCnt;
    return sorted.map(node => {
      const delta = node.data.inoutType === "IN" ? node.data.changeCnt : -node.data.changeCnt;
      cumulative += delta;
      const row = { ...node.data, supplyCnt: delta, totalCnt: cumulative, toolResourceCd: currentResource.resourceCd, toolCnt: currentResource.toolCnt };
      node.setData(row);
      return row;
    });
  };

  const saveToolSupply = () => {
    supplyGridRef.current.api.stopEditing();

    if (!currentResource) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_TOOL_RESOURCE_ROW_SELECT") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(supplyGridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(supplyGridRef.current.api, missingRequired[0]);
      return;
    }

    const rows = computeAndApplyTotals();
    if (rows === null) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("FP_MSG_MINUS_INPUT") });
      return;
    }
    if (rows.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    fpBorService.saveToolSupplies(rows)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveToolSupply(currentResource));
  };

  const removeToolSupply = () => {
    supplyGridRef.current.api.stopEditing();
    const selectedRows = supplyGridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpBorService.removeToolSupplies(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveToolSupply(currentResource));
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveToolSupply();
    else if (action === "DELETE") removeToolSupply();
  };

  // 화면 내부 탭(§11-3): planScope 변경 시에만 좌측 자원 목록 재조회(첫 행 자동 선택 → 우측 이력도 갱신).
  // resourceCdNm은 old와 동일하게 서버 조회 파라미터(이 탭만의 예외 — 다른 탭은 클라 필터).
  useEffect(() => {
    if (!resourceGridRef.current?.api) return;
    retrieveResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields.resourceCdNm]);

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, height: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          {/* old는 이 칼럼에 버튼이 없어서 오른쪽 버튼줄과 높이를 맞추려고 빈 여백(mt 고정값)만
              뒀다 — 라벨 텍스트가 아니라 오른쪽 버튼줄과 같은 높이의 투명 스페이서. */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, visibility: "hidden" }}>
            <AddButton />
          </Box>
          <Box id="fp-tool-supply-resource-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={resourceGridRef}
              rowData={resources}
              {...resourceGridMemo}
              onRowClicked={onResourceRowClicked}
              onGridReady={onResourceGridReady}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <AddButton onClick={insertRow} />
            <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
            <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          </Box>
          {/* old ResultArea(sizes=[50,50], direction="vertical") 대응 — 그리드/차트 1:1. */}
          <Box id="fp-tool-supply-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, pb: 1 }}>
            <AgGridReact
              ref={supplyGridRef}
              rowData={toolSupply}
              {...supplyGridMemo}
              rowSelection={{ ...supplyGridMemo.rowSelection, headerCheckbox: true, checkboxes: true }}
              onGridReady={onSupplyGridReady}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <ToolSupplyTabChart dataSource={toolSupply} initialToolCnt={currentResource?.toolCnt} />
          </Box>
        </Box>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ToolSupplyTab);
